import { useState } from 'react';
import { useLanguage } from '../i18n';
import { frameworkContent, frameworkContentDe } from '../data';
import StyledMarkdown from './StyledMarkdown';
import { ChevronRight, ChevronDown, FileText } from 'lucide-react';

interface TreeNode {
  key: string;
  label: string;
  content?: string;
  children?: TreeNode[];
}

const labels: Record<string, string> = {
  naming: '01 — Brand Naming',
  legal: '02 — Legal Structures',
  brandIdentity: '03 — Brand Identity',
  sops: '04 — SOPs',
  reports: '05 — Reports',
  governance: '06 — Governance',
};

const labelsDe: Record<string, string> = {
  naming: '01 — Markenname',
  legal: '02 — Rechtsformen',
  brandIdentity: '03 — Markenidentität',
  sops: '04 — SOPs',
  reports: '05 — Berichte',
  governance: '06 — Governance',
};

type FrameworkData = Record<string, string | Record<string, unknown>>;

function buildTree(data: FrameworkData, localeLabels: Record<string, string>): TreeNode[] {
  function walk(obj: unknown, key: string): TreeNode {
    if (typeof obj === 'string') {
      return { key, label: key, content: obj };
    }
    if (obj && typeof obj === 'object') {
      const children = Object.entries(obj as FrameworkData).map(([k, v]) => walk(v, `${key}/${k}`));
      return { key, label: localeLabels[key] || key, children };
    }
    return { key, label: key };
  }

  return Object.entries(data).map(([k, v]) => walk(v, k));
}

export default function FrameworkBrowser() {
  const { locale } = useLanguage();
  const [selected, setSelected] = useState<TreeNode | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(
    new Set(['naming', 'legal', 'brandIdentity', 'sops', 'reports', 'governance'])
  );

  const data = locale === 'de' ? frameworkContentDe : frameworkContent;
  const localeLabels = locale === 'de' ? labelsDe : labels;
  const t = translationsForLocale(locale);

  const tree = buildTree(data, localeLabels);

  const toggle = (key: string) => {
    const next = new Set(expanded);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setExpanded(next);
  };

  const renderNode = (node: TreeNode, depth = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expanded.has(node.key);

    return (
      <div key={node.key} style={{ marginLeft: depth * 12 }}>
        <button
          onClick={() => {
            if (hasChildren) toggle(node.key);
            if (node.content) setSelected(node);
          }}
          className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${
            selected?.key === node.key ? 'bg-red-50 text-red-900' : 'hover:bg-mist text-slate'
          }`}
        >
          {hasChildren ? (
            isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />
          ) : (
            <FileText size={14} className="text-ash" />
          )}
          <span className="text-sm truncate">{node.label.replace(/_/g, ' ')}</span>
        </button>
        {hasChildren && isExpanded && (
          <div className="mt-1">{node.children!.map((child) => renderNode(child, depth + 1))}</div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col md:flex-row h-full">
      <nav className="md:w-80 md:shrink-0 bg-white md:border-r border-b md:border-b-0 border-cloud md:overflow-y-auto max-h-[40vh] md:max-h-none overflow-y-auto p-4">
        <h2 className="text-xs font-semibold text-slate uppercase tracking-wider mb-4 px-3">{t.title}</h2>
        <div className="space-y-1">{tree.map((node) => renderNode(node))}</div>
      </nav>

      <div className="flex-1 overflow-y-auto bg-paper p-4 sm:p-6 md:p-8 safe-bottom">
        <div className="max-w-4xl mx-auto">
          {selected?.content ? (
            <div className="bg-white border border-cloud p-4 sm:p-6 md:p-8 shadow-sm">
              <StyledMarkdown content={selected.content} />
            </div>
          ) : (
            <div className="text-center text-slate mt-12 sm:mt-20">
              <FileText size={48} className="mx-auto mb-4 text-cloud" />
              <p>{t.empty}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function translationsForLocale(locale: string) {
  return locale === 'de'
    ? { title: 'Framework-Referenz', empty: 'Wähle ein Dokument in der Seitenleiste, um den Framework-Inhalt anzuzeigen.' }
    : { title: 'Framework Reference', empty: 'Select a document from the sidebar to view the framework content.' };
}
